import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SetLoading } from '../core/loading.state';
import { finalize, tap } from 'rxjs';
import { GuardianInfo } from '../shared';
import { ToastService } from '../core/toast.service';
import { AppFeaturesState } from './app-feature.state';
import { GuardianService } from './guardian.service';
import { AuthState } from './auth.state';

export class GuardianPatchState {
	static readonly type = '@guardianState.patchState';
	constructor(public state: Partial<GuardianStateModel>) {}
}

export class GetGuardianInfo {
	static readonly type = '@guardianState.getGuardianInfo';
}

export class CancelMembership {
	static readonly type = '@guardianState.cancelMembership';
}

interface GuardianStateModel {
	info: GuardianInfo | undefined;
}

@State<GuardianStateModel>({
	name: 'GuardianState',
	defaults: {
		info: undefined,
	},
})
@Injectable()
export class GuardianState {
	constructor(
		private store: Store,
		private guardianService: GuardianService,
		private appFeaturesState: AppFeaturesState,
		private toastService: ToastService
	) {}

	@Selector()
	public static state(state: GuardianStateModel) {
		return state;
	}

	@Selector()
	public static info(state: GuardianStateModel) {
		return state.info;
	}

	get surveysId() {
		return this.store.selectSnapshot(GuardianState.info)?.surveysId;
	}

	@Action(GuardianPatchState)
	patchState(ctx: StateContext<GuardianStateModel>, action: GuardianPatchState) {
		ctx.patchState(action.state);
	}

	@Action(GetGuardianInfo)
	getGuardianInfo(ctx: StateContext<GuardianStateModel>) {
		const guardiansId = this.store.selectSnapshot(AuthState.guardiansId);
		if (guardiansId === undefined) {
			throw Error('Guardian ID is undefined');
		}

		ctx.dispatch(new SetLoading(true, this, true, this.getGuardianInfo));
		return this.guardianService.getGuardianInfo(guardiansId).pipe(
			tap((res) => {
				ctx.patchState({
					info: res.data[0],
				});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.getGuardianInfo)))
		);
	}

	@Action(CancelMembership)
	cancelMembership(ctx: StateContext<GuardianStateModel>) {
		ctx.dispatch(new SetLoading(true, this, true, this.cancelMembership));
		return this.guardianService
			.cancelMembership()
			.pipe(finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.cancelMembership))));
	}
}
